import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  KanbanSquare,
  AlertTriangle,
  Clock,
  CheckSquare,
  ArrowRight,
  Ban,
  Loader,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { kanbanApiService } from '../../services/kanbanApi';
import {
  kanbanTeamsFromPermissions,
  kanbanTeamsFromStaffTeams,
  mergeKanbanTeamLists,
} from '../../utils/kanbanPermissions';
import { KanbanCardTypeBadge } from './KanbanCardTypeBadge';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';
import { SectionHeader } from '../ui/SectionHeader';
import { StatTile } from '../ui/StatTile';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';
import type { KanbanCard, KanbanStats, KanbanActivityLogEntry } from '../../types/kanban';

interface KanbanDashboardWidgetProps {
  userUuid?: string;
  className?: string;
}

/**
 * Dashboard widget showing a summary of the user's kanban workspaces.
 * Displays: assigned cards, overdue count, recent activity, team stats.
 * Drop this into DashboardView for deep kanban integration.
 */
export const KanbanDashboardWidget = ({ userUuid, className }: KanbanDashboardWidgetProps) => {
  const { identity } = useAuth();
  const [myCards, setMyCards] = useState<KanbanCard[]>([]);
  const [stats, setStats] = useState<KanbanStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<KanbanActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const [teamsRes, statsRes, activityRes] = await Promise.allSettled([
          kanbanApiService.listTeams(),
          kanbanApiService.getTeamStats(),
          kanbanApiService.getRecentActivity({ limit: 5 }),
        ]);

        if (cancelled) return;

        if (teamsRes.status === 'fulfilled') {
          const fromApi = teamsRes.value.teams;
          const fromJwt = kanbanTeamsFromPermissions(identity?.raw?.custom?.permissions);
          const fromStaff = kanbanTeamsFromStaffTeams(identity?.raw?.custom?.staff_teams);
          setTeams(mergeKanbanTeamLists(mergeKanbanTeamLists(fromApi, fromJwt), fromStaff));
        }

        if (statsRes.status === 'fulfilled') setStats(statsRes.value);
        if (activityRes.status === 'fulfilled') {
          const rows = activityRes.value;
          setRecentActivity(Array.isArray(rows) ? rows : []);
        }

        // Load user's assigned cards if we have their UUID
        if (userUuid) {
          try {
            const cardsRes = await kanbanApiService.getCardsByAssignee(userUuid, { limit: 5 });
            if (!cancelled) setMyCards(Array.isArray(cardsRes.results) ? cardsRes.results : []);
          } catch { /* may not have kanban access */ }
        }
      } catch {
        // Kanban may not be available for this user — fail silently
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [userUuid, identity]);

  // Don't render if user has no kanban access
  if (!loading && teams.length === 0 && !stats) return null;

  if (loading) {
    return (
      <section className={cn('space-y-3', className)} aria-busy="true">
        <SectionHeader
          title="Workspaces"
          icon={KanbanSquare}
          as="h2"
          actions={<Loader className="w-4 h-4 animate-spin text-glass-400" aria-label="Loading workspaces" />}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatTile label="Cards" value={null} icon={CheckSquare} loading />
          <StatTile label="Overdue" value={null} icon={Clock} loading />
          <StatTile label="Blocked" value={null} icon={Ban} loading />
        </div>
        <div className="glass-card p-4 md:p-5 space-y-3">
          <SkeletonLoader className="h-3 w-20" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <SkeletonLoader className="h-5 w-8 rounded-full" />
              <SkeletonLoader className="h-4 flex-1" />
              <SkeletonLoader className="h-5 w-9 rounded-full" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  const overdueCount = stats?.cards_overdue ?? 0;
  const blockedCount = stats?.cards_blocked ?? 0;
  const totalCards = stats?.total_cards ?? 0;
  const hasLists = myCards.length > 0 || recentActivity.length > 0;

  return (
    <section className={cn('space-y-3', className)}>
      {/* Header */}
      <SectionHeader
        title="Workspaces"
        icon={KanbanSquare}
        as="h2"
        actions={
          <Link to="/workspaces" className="glass-button-sm">
            View All <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile label="Cards" value={totalCards.toLocaleString()} icon={CheckSquare} tone="accent" />
        <StatTile
          label="Overdue"
          value={<span className={cn(overdueCount > 0 && 'text-orange-400')}>{overdueCount.toLocaleString()}</span>}
          icon={Clock}
          tone={overdueCount > 0 ? 'orange' : 'neutral'}
        />
        <StatTile
          label="Blocked"
          value={<span className={cn(blockedCount > 0 && 'text-red-400')}>{blockedCount.toLocaleString()}</span>}
          icon={Ban}
          tone={blockedCount > 0 ? 'danger' : 'neutral'}
        />
      </div>

      {(hasLists || teams.length > 0) && (
        <div className="glass-card p-4 md:p-5 space-y-4">
          {/* My Assigned Cards */}
          {myCards.length > 0 && (
            <div>
              <h4 className="eyebrow mb-1">My Cards</h4>
              <div className="divide-y divide-glass-200/10">
                {myCards.slice(0, 3).map(card => (
                  <Link
                    key={card.id}
                    to={`/workspaces/${card.board_id}`}
                    className="flex items-center gap-2 -mx-2 px-2 py-2 rounded-lg hover:bg-glass-200/10 min-w-0"
                    title={card.title}
                  >
                    <KanbanCardTypeBadge type={card.card_type} showLabel={false} />
                    <span className="text-sm text-white truncate flex-1 min-w-0">{card.title}</span>
                    {card.priority !== 5 && <KanbanPriorityBadge priority={card.priority} />}
                    {card.is_blocked === 1 && <Ban className="w-3.5 h-3.5 text-red-400 shrink-0" aria-label="Blocked" />}
                    {card.due_date && card.due_date < Date.now() && !card.completed_at && (
                      <AlertTriangle className="w-3.5 h-3.5 text-orange-400 shrink-0" aria-label="Overdue" />
                    )}
                  </Link>
                ))}
              </div>
              {myCards.length > 3 && (
                <Link to="/workspaces" className="link text-xs block text-center py-1.5">
                  +{myCards.length - 3} more cards
                </Link>
              )}
            </div>
          )}

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <div>
              <h4 className="eyebrow mb-1">Recent Activity</h4>
              <div className="divide-y divide-glass-200/10">
                {recentActivity.slice(0, 3).map(entry => (
                  <div key={entry.id} className="flex items-baseline justify-between gap-3 py-2 text-xs">
                    <span className="text-glass-300 min-w-0">{entry.description}</span>
                    <span className="text-glass-500 tabular-nums whitespace-nowrap shrink-0">
                      {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team Quick Access */}
          {teams.length > 0 && (
            <div className={cn('flex flex-wrap gap-1.5', hasLists && 'pt-3 border-t border-glass-200/10')}>
              {teams.map(team => (
                <Link
                  key={team}
                  to="/workspaces"
                  className="chip hover:text-white hover:border-glass-200/25"
                >
                  {team.replace('team', '')}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
